import { LoadingArea, Spinner } from '@consigli/facade';
import {
  useProjectId,
  usePackageId,
  useCurrentLanguage,
  useFindings,
  useLazyGetFindingsQuery,
  useGetFindingsUniqueBlobsQuery,
  useGetFindingsStatusCountQuery,
  useGetFindingsConflictTypeCountsQuery,
  useGetFindingsCommentersQuery,
  useGetFindingsUniqueLabelsQuery,
  useServiceNameOrNull,
} from '@consigli/hooks';
import { FindingStatus, ServiceName } from '@consigli/types';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentViewerDrawer } from '@/features/document-viewer/fullscreen/finding/document-viewer-drawer';
import {
  useViewerContext,
  ViewerMode,
  useFullscreenViewerHost,
} from '@/features/document-viewer/use-viewer-context';
import { useBidReviewContractors } from '@/features/package-creation/bid-review/use-bid-review-contractors';
import { WorkspaceSidebar } from '@/layouts/workspace-layout/workspace-sidebar';
import { useDemoMode } from '@/util/demo-mode';
import { snapCenterToCursor } from '@/util/dragging-util';

import { FindingsCommentSectionWrapper } from './findings-comment-section-wrapper';
import { FindingsContent } from './findings-content';
import { useFindingsContext } from './findings-context';
import { FindingsSidebar } from './findings-sidebar';
export interface FindingBlob {
  id: string;
  name: string;
}

export const FindingsWrapper: FC = () => {
  useFullscreenViewerHost();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const currentLanguage = useCurrentLanguage();
  const { t } = useTranslation();
  const { mode, setMode } = useViewerContext();
  const { demoView, adminFeaturesEnabled } = useDemoMode();

  const {
    page,
    pageSize,
    selectedStatusTab,
    setSelectedStatusTab,
    selectedConflictCategory,
    selectedConflictType,
    selectedBlobIds,
    selectedContractor,
    debouncedSearchText,
    selectedCommenterIds,
    selectedLabels,
  } = useFindingsContext();

  // The "Offer (Bid Review)" service only surfaces findings that sit on a
  // bid/contractor document — everything whose blob `reference` is neither the
  // tender (primary_reference) nor the blank leveling template (bid_levelling).
  // This is resolved server-side via `bidDocumentsOnly`; enumerating every bid
  // blob id into the query string overran the gateway's request-line limit on
  // packages with many documents. Scoped to BID_REVIEW only.
  const serviceName = useServiceNameOrNull();
  const isBidReview = serviceName === ServiceName.BID_REVIEW;

  const { contractors, isLoading: contractorsLoading } = useBidReviewContractors({
    skip: !isBidReview,
  });

  // Legacy OFFER packages predate per-bid `reference` tagging, so they resolve no
  // contractors. Scoping their findings to bid documents would hide every finding
  // (nothing is tagged as a bid), so those packages skip the bid-documents-only
  // default and list all findings — matching the legacy Offer/Insight view. While
  // the contractors query is still loading we assume the new structure, to avoid a
  // flash of tender/leveling findings on a real Bid Review package.
  const isLegacyOfferPackage = isBidReview && !contractorsLoading && contractors.length === 0;

  // A manual document filter wins; otherwise Bid Review defaults to bid
  // documents only (all contractors), resolved server-side. When a single
  // contractor is selected, the `reference` filter already scopes to that bid.
  const effectiveBlobIds = selectedBlobIds;
  // `true` only in the default Bid Review case; otherwise `undefined` so the
  // param is omitted entirely and every other service's requests are unchanged.
  const bidDocumentsOnly =
    isBidReview && !isLegacyOfferPackage && !selectedContractor && selectedBlobIds.length === 0
      ? true
      : undefined;

  const {
    findings,
    paginatedCount,
    isLoading,
    isFetching: isFetchingFindings,
  } = useFindings({
    projectId,
    packageId,
    page: page,
    pageSize,
    status: selectedStatusTab,
    demoView,
    conflictCategory: selectedConflictCategory,
    conflictType: selectedConflictType,
    blobIds: effectiveBlobIds,
    bidDocumentsOnly,
    reference: selectedContractor,
    search: debouncedSearchText,
    language: currentLanguage,
    commenters: selectedCommenterIds,
    labels: selectedLabels,
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(PointerSensor),
  );

  const itemsOnPage = useMemo(() => {
    return page * pageSize > paginatedCount ? paginatedCount % pageSize : pageSize;
  }, [page, pageSize, paginatedCount]);

  const [getFindingsForExport, { isLoading: isDownloadingFindingsForExport }] =
    useLazyGetFindingsQuery();

  const downloadFindingsForExport = useCallback(
    async (isFilterNeeded: boolean) => {
      const findingsResponse = await getFindingsForExport({
        projectId,
        packageId,
        page: 'all',
        demoView,
        conflictCategory: isFilterNeeded ? selectedConflictCategory : undefined,
        conflictType: isFilterNeeded ? selectedConflictType : undefined,
        blobIds: isFilterNeeded ? effectiveBlobIds : undefined,
        bidDocumentsOnly: isFilterNeeded
          ? bidDocumentsOnly
          : isBidReview && !isLegacyOfferPackage
            ? true
            : undefined,
        reference: isFilterNeeded ? selectedContractor : undefined,
        commenters: isFilterNeeded ? selectedCommenterIds : undefined,
        labels: isFilterNeeded ? selectedLabels : undefined,
      }).unwrap();
      const findingsNotRelevantRemoved = findingsResponse.results.filter((finding) => {
        return finding.status !== FindingStatus.NOT_RELEVANT;
      });
      return findingsNotRelevantRemoved;
    },
    [
      getFindingsForExport,
      projectId,
      packageId,
      demoView,
      selectedConflictCategory,
      selectedConflictType,
      effectiveBlobIds,
      isBidReview,
      isLegacyOfferPackage,
      bidDocumentsOnly,
      selectedContractor,
      selectedCommenterIds,
      selectedLabels,
    ],
  );

  const { data: commenters } = useGetFindingsCommentersQuery({
    projectId,
    packageId,
    demoView,
    status: selectedStatusTab,
    conflictCategory: selectedConflictCategory,
    conflictType: selectedConflictType,
    blobIds: effectiveBlobIds,
    reference: selectedContractor,
    labels: selectedLabels,
  });

  const { data: uniqueLabels = [] } = useGetFindingsUniqueLabelsQuery({
    projectId,
    packageId,
    demoView,
    status: selectedStatusTab,
    conflictCategory: selectedConflictCategory,
    conflictType: selectedConflictType,
    commenters: selectedCommenterIds,
    blobIds: effectiveBlobIds,
    bidDocumentsOnly,
  });

  const { data: uniqueBlobs } = useGetFindingsUniqueBlobsQuery({
    projectId,
    packageId,
    demoView,
    status: selectedStatusTab,
    conflictCategory: selectedConflictCategory,
    conflictType: selectedConflictType,
    commenters: selectedCommenterIds,
    labels: selectedLabels,
  });

  const {
    data: statusCount,
    isFetching: isFetchingStatusCount,
    isLoading: isLoadingStatusCount,
  } = useGetFindingsStatusCountQuery({
    projectId,
    packageId,
    demoView,
    conflictCategory: selectedConflictCategory,
    conflictType: selectedConflictType,
    blobIds: effectiveBlobIds,
    bidDocumentsOnly,
    reference: selectedContractor,
    commenters: selectedCommenterIds,
    labels: selectedLabels,
  });

  const {
    data: conflictTypeCounts,
    isFetching: isFetchingConflictTypeCount,
    isLoading: isLoadingConflictTypeCount,
  } = useGetFindingsConflictTypeCountsQuery({
    projectId,
    packageId,
    blobIds: effectiveBlobIds,
    bidDocumentsOnly,
    reference: selectedContractor,
    commenters: selectedCommenterIds,
    labels: selectedLabels,
  });

  const statusTabs = useMemo(() => {
    const preferredTabOrder = [
      FindingStatus.ACTIVE,
      FindingStatus.PROCESSING,
      FindingStatus.PROCESSED,
      FindingStatus.NOT_RELEVANT,
      FindingStatus.DELETED,
    ];
    if (statusCount) {
      let keys = Object.keys(statusCount) as FindingStatus[];
      if (!adminFeaturesEnabled) {
        keys = keys.filter((status) => status !== FindingStatus.DELETED);
      }
      return keys.sort((a, b) => {
        return preferredTabOrder.indexOf(a) - preferredTabOrder.indexOf(b);
      });
    }
    return [];
  }, [statusCount, adminFeaturesEnabled]);

  const visibleStatusCount = useMemo(() => {
    if (!statusCount) return {};
    if (adminFeaturesEnabled) return statusCount;
    const filtered: Record<string, number> = { ...statusCount };
    delete filtered[FindingStatus.DELETED];
    return filtered;
  }, [statusCount, adminFeaturesEnabled]);

  useEffect(() => {
    if (statusTabs.length > 0 && !statusTabs.includes(selectedStatusTab)) {
      setSelectedStatusTab(statusTabs[0] as FindingStatus);
    }
  }, [statusTabs, selectedStatusTab, setSelectedStatusTab]);

  useEffect(() => {
    return () => {
      setMode(ViewerMode.Closed);
    };
  }, [setMode]);

  // A single centered loader for the initial page load. Waiting on the status
  // and conflict-type counts too avoids their separate left-aligned spinners
  // flashing once the findings resolve first. These counts share the findings
  // filter args, so this adds no extra blanking on pagination/tab/filter changes.
  if (isLoading || isLoadingStatusCount || isLoadingConflictTypeCount)
    return <LoadingArea title="Loading findings" />;

  // Status counts omit statuses with no findings, so a filter matching nothing
  // yields no tabs; keep the filters mounted so the user can change them.
  const hasActiveFilters =
    !!selectedConflictCategory ||
    !!selectedConflictType ||
    !!selectedContractor ||
    selectedBlobIds.length > 0 ||
    selectedCommenterIds.length > 0 ||
    selectedLabels.length > 0 ||
    debouncedSearchText.length > 0;

  if (findings.length === 0 && statusTabs.length === 0 && !hasActiveFilters)
    return (
      <div className="bg-day-light-3 w-full h-full flex flex-col items-center justify-center">
        <div className="text-day-neutral-subtle text-xl font-medium py-4">
          {t('findings.no-findings')}
        </div>
      </div>
    );
  return (
    <>
      <DndContext sensors={sensors} modifiers={[snapCenterToCursor, restrictToParentElement]}>
        <div className="flex relative">
          <WorkspaceSidebar
            sidebarExpanded={false}
            className="h-[calc(100vh-9rem)] 2xl:h-[calc(100vh-13rem)]"
          >
            {conflictTypeCounts ? (
              <FindingsSidebar
                conflictTypeCounts={conflictTypeCounts}
                downloadFindingsForExport={downloadFindingsForExport}
                isDownloadingFindingsForExport={isDownloadingFindingsForExport}
              />
            ) : isFetchingConflictTypeCount ? (
              <Spinner />
            ) : null}
          </WorkspaceSidebar>
          {statusCount && uniqueBlobs && commenters && (
            <FindingsContent
              findings={findings}
              findingsCount={paginatedCount}
              contractors={contractors}
              uniqueBlobs={uniqueBlobs}
              commenters={commenters}
              uniqueLabels={uniqueLabels}
              statusTabs={statusTabs as FindingStatus[]}
              statusCount={visibleStatusCount}
              itemsOnPage={itemsOnPage}
              isFetchingFindings={isFetchingFindings}
              isFetchingStatusCount={isFetchingStatusCount}
              downloadFindingsForExport={downloadFindingsForExport}
              isDownloadingFindingsForExport={isDownloadingFindingsForExport}
            />
          )}
          {mode === ViewerMode.DocumentFullscreen && (
            <DocumentViewerDrawer itemsOnPage={itemsOnPage} />
          )}
          {mode === ViewerMode.CommentPreview && <FindingsCommentSectionWrapper />}
        </div>
      </DndContext>
    </>
  );
};
